import { Box, Button, Container, Link, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Dropzone, { DropEvent } from 'react-dropzone';
import React, { useEffect, useState } from 'react';
import { SiteDTO } from '@beable/roster-backend-client-v1';
import { uploadArchive } from '../../services/FileUploadService';
import { getStyles } from './styles';
import { useNavigate } from 'react-router';

const initialFilesValue: File[] = [];

interface FileUploadProps {
  site: SiteDTO;
  customerId: string;
  prompt: string;
  type?: string;
}

export default function FileUpload({ site, customerId, prompt, type }: FileUploadProps) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [selectedFiles, setSelectedFiles] = useState(initialFilesValue);
  const [fileName, setFilename] = useState('');
  const [fileErr, setFileErr] = useState('');
  const [fileSuccess, setFileSuccess] = useState('');
  const [disableSend, setDisableSend] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    resetState();
  }, [site]);

  const fileNamePattern = /[`!@#$%^&*()+=[\]{};':"\\|,<>/?~]/;

  const goToBatchStatus = () => {
    navigate(`/batch-status`);
  };

  const fileTooLarge = (file: File) => {
    return file.size > 35000000;
  }
  const setErrorsOnLoad = (msg: string) => {
    setFilename('');
    setSelectedFiles(initialFilesValue);
    setFileErr(msg);
    setFileSuccess('');
    setDisableSend(true);
  }

  const handleFilesSelect = (files: File[], event: DropEvent) => {
    event.preventDefault();

    if (files.length === 1) {
      if (!files[0].name.includes('.zip')) {
        setErrorsOnLoad('Invalid file format!');
        return;
      }
      if (fileNamePattern.test(files[0].name)) {
        setErrorsOnLoad('Filename must contain only alphanumeric values!');
        return;
      }

      if (!fileTooLarge(files[0])) {
        setFilename(files[0].name);
        setSelectedFiles(files);
        setFileErr('');
        setFileSuccess('');
        if (customerId && site.id) setDisableSend(false);
        return;
      } else if (fileTooLarge(files[0])) {
        setErrorsOnLoad('Max file size 35mb.');
        return;
      }

    } else {
      setErrorsOnLoad('Invalid file type!');
      return;
    }
  };

  const handleFileValidation = async () => {
    setDisableSend(true);
    const uploadResult = await uploadArchive(selectedFiles[0], site, type);
    resetState();
    if (uploadResult && uploadResult.status === 201) {
      setFileSuccess('Uploaded successfully!');
    } else {
      setFileErr('Upload failed!');
    }
  };

  const resetState = () => {
    setFilename('');
    setSelectedFiles(initialFilesValue);
    setFileErr('');
    setFileSuccess('');
    setDisableSend(true);
  }


  return (
    <>
      <Container sx={styles.dfb}>
        <Container sx={styles.dfe}>
          <Button data-cy='clearButton' variant="contained" onClick={resetState}>Clear</Button>
          <Button sx={styles.ml2} data-cy='uploadFileButton' variant="contained" color="primary" disabled={disableSend} onClick={handleFileValidation}>
            Upload file
          </Button>
        </Container>
      </Container>
      <Dropzone onDrop={(acceptedFiles, _, event) => { handleFilesSelect(acceptedFiles, event) }}>
        {({ getRootProps, getInputProps }) => (
          <section>
            <Box sx={styles.fileUpload as React.CSSProperties} {...getRootProps()}>
              <input {...getInputProps()} />
              {
                selectedFiles.length > 0 ? <Typography component="p" sx={styles.lt}>Your file: {fileName}</Typography> :
                  <Typography component="p" sx={styles.lt}>{prompt}</Typography>
              }
            </Box>
          </section>
        )}
      </Dropzone>
      {fileErr ? <Typography data-cy='errorMessage' sx={{ ...styles.errMsg, textAlign: 'left' }} component="p">Error: {fileErr}</Typography> : undefined}
      {fileSuccess ? (
        <>
          <Typography
            sx={{ ...styles.successMsg, textAlign: 'left' }}
            component="p"
          >
            Status: {fileSuccess}

            <Link
              onClick={() => {
                goToBatchStatus();
              }}
              component="button"
              variant="body2"
              sx={{ marginLeft: '4px' }}
              underline="hover">
              Go to Batch Status
            </Link>
          </Typography>
        </>
      )
        : undefined}
    </>
  );

}